import * as React from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import { History, PencilLine, RefreshCw } from "lucide-react";
import { Badge } from "@/lib/shadcn/ui/badge";

/**
 * AG-Grid cell renderers for the HostHub-sourced reservations grid (feature
 * hosthub-reservation-ingestion; FR- refs below reference its spec.md). All
 * indicators are text-bearing, never colour-only (WCAG 1.4.1).
 *
 * The fields read (`origin`, `isCancelled`, `needsRefile`) are OPTIONAL in the
 * generated type, so every renderer reads defensively with a safe default.
 */

/** Row shape these renderers consume (the AG-Grid `data`). */
export interface ReservationGridCellData {
    reservationCode: string;
    origin?: string;
    isCancelled?: boolean;
    needsRefile?: boolean;
    isSyncFrozen?: boolean;
}

/**
 * The words these cells put on screen — the single source of truth for them.
 *
 * Each column's Set Filter must offer the SAME labels its cell renders, but
 * AG-Grid builds that filter list from raw values and never consults a
 * `cellRenderer` (see GOTCHAS.md), so the column has to restate the mapping via
 * `filterParams.valueFormatter`. These constants are what keep the two ends from
 * drifting: the renderers below and the colDefs in `adminReservationsPage.tsx`
 * both read from here, so a wording change lands in the cell AND its filter.
 */
export const ORIGIN_LABELS = { hosthub: "HostHub", manual: "Manual" } as const;

export const STATUS_LABELS = { cancelled: "Cancelled", active: "Active" } as const;

export const NEEDS_REFILE_LABELS = {
    needed: "Needs re-file",
    /**
     * Filter-only. The CELL renders a bare em-dash for the steady state (a
     * visual "nothing here" placeholder, deliberately not a word), but a filter
     * item labelled "—" is unpickable, so the filter spells it out. This is the
     * one place where the cell and its filter intentionally differ — keep the
     * em-dash in `NeedsRefileBadgeCell` and this wording in step with it.
     */
    upToDate: "Up to date",
} as const;

/**
 * Sync — whether the reservation still tracks HostHub
 * (manual-reservation-override/FR-027). `tracking` is filter-only, on the same
 * grounds as `NEEDS_REFILE_LABELS.upToDate`: the cell renders an em-dash for the
 * steady state, which no one could pick out of a filter list.
 */
export const SYNC_LABELS = { frozen: "Sync frozen", tracking: "Tracking HostHub" } as const;

type CellProps = CustomCellRendererProps<ReservationGridCellData>;

/**
 * Origin — `HostHub` (navy) vs `Manual` (gray). HostHub is the source of truth
 * for ingested reservations; manual rows are the legacy/operator-entered ones.
 */
export const OriginBadgeCell: React.FC<CellProps> = ({ data }) => {
    const isHostHub = (data?.origin ?? "manual") === "hosthub";
    return isHostHub ? (
        <Badge data-testid="origin-badge" data-origin="hosthub" className="border-transparent bg-blue-900 text-white">
            {ORIGIN_LABELS.hosthub}
        </Badge>
    ) : (
        <Badge data-testid="origin-badge" data-origin="manual" variant="secondary">
            {ORIGIN_LABELS.manual}
        </Badge>
    );
};

/**
 * Status — `Active` (plain) vs `Cancelled` (red). Cancelled rows stay shown and
 * selectable (their compensation can still be revenue, FR-009); the row body is
 * muted via `text-muted-foreground` at the row level, but the status is conveyed
 * by this text badge, not colour alone.
 */
export const StatusBadgeCell: React.FC<CellProps> = ({ data }) => {
    const cancelled = data?.isCancelled ?? false;
    return cancelled ? (
        <Badge data-testid="status-badge" data-status="cancelled" className="border-transparent bg-red-700 text-white">
            {STATUS_LABELS.cancelled}
        </Badge>
    ) : (
        <Badge data-testid="status-badge" data-status="active" variant="outline">
            {STATUS_LABELS.active}
        </Badge>
    );
};

/**
 * Re-file — amber `Needs re-file` badge when the reservation changed after it
 * was filed with AADE/STR (FR-012). Empty otherwise (no badge for the steady
 * state). Text-bearing, not colour-only.
 */
export const NeedsRefileBadgeCell: React.FC<CellProps> = ({ data }) => {
    if (!(data?.needsRefile ?? false)) {
        return <span aria-hidden="true">—</span>;
    }
    return (
        <Badge
            data-testid="needs-refile-badge"
            className="border-transparent bg-amber-100 text-amber-800"
        >
            {NEEDS_REFILE_LABELS.needed}
        </Badge>
    );
};

/**
 * Sync — an amber `Sync frozen` badge for a reservation that no longer follows
 * HostHub (manual-reservation-cancellation/FR-026). Status only: the action that
 * hands it back to HostHub lives in the Actions column. Text-bearing, not
 * colour-only.
 */
export const SyncStatusCell: React.FC<CellProps> = ({ data }) => {
    if (!data?.isSyncFrozen) {
        return <span aria-hidden="true">—</span>;
    }
    return (
        <Badge data-testid="sync-frozen-badge" className="border-transparent bg-amber-100 text-amber-800">
            {SYNC_LABELS.frozen}
        </Badge>
    );
};

const ICON_BUTTON_CLASS =
    "inline-flex items-center justify-center rounded-md p-1 text-slate-600 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";

/**
 * Actions — every action a row offers, gathered in one pinned column
 * (manual-reservation-cancellation/FR-022). Edit and history are always there;
 * resume-sync only on a sync-frozen row (FR-025).
 */
export interface RowActionsCellProps extends CellProps {
    onOverride: (data: ReservationGridCellData) => void;
    onOpenHistory: (data: ReservationGridCellData) => void;
    onResumeSync: (data: ReservationGridCellData) => void;
}

export const RowActionsCell: React.FC<RowActionsCellProps> = ({
    data,
    onOverride,
    onOpenHistory,
    onResumeSync,
}) => {
    if (!data) {
        return null;
    }
    const code = data.reservationCode;
    return (
        <div className="flex items-center gap-1">
            <button
                type="button"
                data-testid={`edit-button-${code}`}
                aria-label={`Edit reservation ${code}`}
                onClick={() => onOverride(data)}
                className={ICON_BUTTON_CLASS}
            >
                <PencilLine className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
                type="button"
                data-testid={`history-button-${code}`}
                aria-label={`View version history for reservation ${code}`}
                onClick={() => onOpenHistory(data)}
                className={ICON_BUTTON_CLASS}
            >
                <History className="h-4 w-4" aria-hidden="true" />
            </button>
            {data.isSyncFrozen && (
                <button
                    type="button"
                    data-testid={`resume-sync-button-${code}`}
                    aria-label={`Resume HostHub sync for reservation ${code}`}
                    onClick={() => onResumeSync(data)}
                    className={ICON_BUTTON_CLASS}
                >
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                </button>
            )}
        </div>
    );
};
